import { UserRole } from 'types';

import {
  CAREER_ROLES,
  FINANCE_EXPENSE_ROLES,
  PAYMENT_ACCESS_ROLES,
  SURVEY_ROLES,
  ZOOM_ROLES,
} from './paletteRoles';

/**
 * Карточка группы (`/education/groups/:id/details`) — кто какой её запрос
 * ЧИТАЕТ на бэкенде. Маршрут к карточке шире любого из этих списков
 * (Admin, AccountManager, Methodist, SalesManager, HRManager, FinanceManager),
 * а отказ сервера приходит ошибкой без кода, которую блок раньше рисовал как
 * «пусто» / «0 PLN» / «проблем не найдено». Решение владельца 06.10.2026:
 * что роль не читает — прячем (и запрос не шлём), права бэкенда не расширяем.
 *
 * Каждый список — зеркало ОДНОЙ константы бэкенда (`tmsis_py_server/tmsis/…`);
 * совпадающий по смыслу список приложения берём как есть, а не копируем:
 * две копии одного правила расходятся молча.
 */

/** `settings.MANAGEMENT_ROLES` (`tmsis/settings.py`): ростер (`agreements_for`), взносы, Slack, заметки, задачи. */
export const MANAGEMENT_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
  UserRole.FINANCIER,
];

/**
 * `resolve_group_revenue_aggregates` (`groups/schema/schema.py`) — свой список
 * в декораторе: Admin, FinanceManager, AccountManager и (решение владельца
 * 06.10.2026) SalesManager.
 */
export const GROUP_REVENUE_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCIER,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
];

/**
 * `resolve_group_attendance_summary` / `resolve_group_performance_summary`
 * (`groups/schema/schema.py`) — списки в декораторах: Admin, FinanceManager,
 * AccountManager, Methodist, Teacher.
 */
export const GROUP_STUDY_SUMMARY_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCIER,
  UserRole.ACCOUNTANT,
  UserRole.METHODIST,
  UserRole.TEACHER,
];

/** `ZOOM_ACCESS_ROLES` (`zoom_recordings/schema/schema.py`) = `ZOOM_ROLES` приложения. */
export const ZOOM_ACCESS_ROLES: UserRole[] = ZOOM_ROLES;

/** `ZOOM_GROUP_TRIGGER_ROLES` (`zoom_recordings/schema/mutations.py`) — «Обработать сейчас». */
export const ZOOM_GROUP_TRIGGER_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.METHODIST,
];

/** `PANDADOC_ACCESS_ROLES` (`pandadoc/schema/schema.py`). */
export const PANDADOC_ACCESS_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
];

/** `MATERIALS_READ_ROLES` (`materials/schema/schema.py`). */
export const MATERIALS_READ_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.METHODIST,
  UserRole.TEACHER,
  UserRole.ACCOUNTANT,
];

/** `MATERIALS_WRITE_ROLES` (`materials/schema/schema.py`) — создать, править, удалить. */
export const MATERIALS_WRITE_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.METHODIST,
  UserRole.TEACHER,
];

/** `HOMEWORK_ERP_ROLES` (`homeworks/schema/schema.py`). */
export const HOMEWORK_ERP_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.METHODIST,
];

/** `SURVEY_MANAGER_ROLES` (`surveys/schema/schema.py`) = `SURVEY_ROLES` приложения. */
export const SURVEY_MANAGER_ROLES: UserRole[] = SURVEY_ROLES;

/** `TEACHER_INSIGHT_ROLES` (`surveys/schema/schema.py`) — `surveyAnalytics`: менеджеры опросов + HR. */
export const TEACHER_INSIGHT_ROLES: UserRole[] = [
  ...SURVEY_ROLES,
  UserRole.HRMANAGER,
];

/**
 * `MAILING_ACCESS_ROLES` (`sendgrid_mail/schema/schema.py`) — SendGrid:
 * `sendgridTemplates`, `sendgridGroupList`, `groupMailCandidates`, `mailJobs`.
 * SMS группе — отдельно, `SMS_ROLES`.
 */
export const MAILING_ACCESS_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.FINANCIER,
];

/** `FAKTUROWNIA_ACCESS_ROLES` (`fakturownia/schema/schema.py`) — фактуры и их агрегаты. */
export const FAKTUROWNIA_ACCESS_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCIER,
  UserRole.SALESMANAGER,
  UserRole.ACCOUNTANT,
];

/** `EXPENSES_ACCESS_ROLES` (`expenses/schema/schema.py`) = `FINANCE_EXPENSE_ROLES` приложения. */
export const EXPENSES_ACCESS_ROLES: UserRole[] = FINANCE_EXPENSE_ROLES;

/** `PAYMENT_ACCESS_ROLES` (`students/schema/payments.py`) — `debtorCandidates`. */
export const DEBTOR_CANDIDATES_ROLES: UserRole[] = PAYMENT_ACCESS_ROLES;

/** `DIPLOMA_ERP_ROLES` (`diplomas/schema/schema.py`) — скачать, прикрепить, удалить. */
export const DIPLOMA_ERP_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.METHODIST,
];

/** `DIPLOMA_ERP_READ_ROLES` (`diplomas/schema/schema.py`) — `groupDiplomas`: + FinanceManager. */
export const DIPLOMA_ERP_READ_ROLES: UserRole[] = [
  ...DIPLOMA_ERP_ROLES,
  UserRole.FINANCIER,
];

/**
 * Поля `GroupType`, закрытые ролями на уровне ПОЛЯ
 * (`groups/schema/object_types.py`, `permissions_required_resolver` у
 * `price`, `currency`, `teachers`, `deleteAvailable`): `MANAGEMENT_ROLES`.
 * Методист читает группы (`allGroups`, `group`), но эти поля — нет: запрос,
 * где они есть, приходит с ошибкой «The request contains a field for which
 * you do not have permission». `contributionsSum` вне списка приходит
 * `null` (`erp_management_viewer`). Операции включают эти поля через
 * `@include(if: $withStaffFields)`.
 */
export const GROUP_STAFF_FIELDS_ROLES: UserRole[] = MANAGEMENT_ROLES;

/**
 * `resolve_meta_groups` (`groups/schema/schema.py`) — счётчик групп и
 * журналов на плитках «Обучения»: Admin, AccountManager, SalesManager,
 * HRManager (методиста и финансиста нет).
 */
export const META_GROUPS_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
];

/** `CAREER_ROLES` (`career/schema/schema.py`) — `careerGroupSkills`. */
export const GROUP_SKILLS_ROLES: UserRole[] = CAREER_ROLES;
